import {
  getIndexPagination,
  getIndexPaginationSearchParams,
  normalizeIndexPagination,
  INDEX_PAGE_SIZES,
} from '@shared/lib/pagination';
import { PageContainer } from '@shared/ui';
import {
  CandidateList,
  CandidateListFilters,
  type CandidateFiltersQuery,
} from '@features/candidate/candidate-list';
import { useEffect, useState } from 'react';
import { useExportCandidates, useGetCandidates } from '@entities/candidates';
import { PaginateType, type SortQuery } from '@shared/types';
import { Button } from '@mantine/core';
import { IconFileExport } from '@tabler/icons-react';
import { useDataExport } from '@shared/ui/modal/hooks/useDataExport';
import type { ExportDataFunction } from '@shared/ui/modal/hooks/useDataExport/types';
import { useSearchParams } from 'react-router';

export const CandidateListPage = () => {
  const [filter, setFilter] = useState<CandidateFiltersQuery>({});
  const [sort, setSort] = useState<SortQuery>({ id: 'desc' });

  const { mutateAsync: exportCandidates, isPending } = useExportCandidates();

  const { openChangeDataExport } = useDataExport({
    text: 'Экспортируйте всех кандидатов или созданных в заданный период  ',
    title: 'Экспорт кандидатов',
    type: 'candidate',
    exportData: exportCandidates as unknown as ExportDataFunction,
    isPending,
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const { page, pageSize } = getIndexPagination(searchParams);

  useEffect(() => {
    const normalized = normalizeIndexPagination(searchParams);
    if (normalized.toString() !== searchParams.toString()) {
      setSearchParams(normalized, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const setPage = (nextPage: number) => {
    setSearchParams(
      getIndexPaginationSearchParams(searchParams, nextPage, pageSize)
    );
  };
  const setPageSize = (size: number) => {
    setSearchParams(getIndexPaginationSearchParams(searchParams, 1, size));
  };

  const {
    data: candidates,
    isLoading,
    isFetching,
  } = useGetCandidates({
    filter,
    sort,
    perPage: pageSize,
    page,
    paginate: PaginateType.DEFAULT,
  });

  const handleSortChange = (field: string, direction?: 'asc' | 'desc') => {
    if (!direction) {
      setSort({ id: 'desc' });
      return;
    }

    setSort({
      [field]: direction,
    });
  };

  return (
    <PageContainer
      title='Кандидаты'
      actionButton={
        <Button
          onClick={openChangeDataExport}
          variant='default'
          leftSection={<IconFileExport />}
          size='md'
        >
          Экспорт
        </Button>
      }
    >
      <CandidateListFilters setFilter={setFilter} pageSize={pageSize} />

      <CandidateList
        candidates={candidates?.data || []}
        page={page}
        setPage={setPage}
        onRecordsPerPageChange={setPageSize}
        recordsPerPageOptions={INDEX_PAGE_SIZES}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isFilledFilters={Boolean(Object.keys(filter).length)}
        onSortChange={handleSortChange}
        currentSort={sort}
        isLoading={isLoading || isFetching}
        totalRecords={candidates?.pagination?.total || 0}
      />
    </PageContainer>
  );
};
